// Pure helpers for the Marketing page: summary counts, row labels and the
// channel lock rule. No React, so they are tested directly.
import { tierAllows } from "../../hooks/usePlanTier";
import { segmentLabelKey } from "../dashboard/segments";
import { formatNumber } from "../dashboard/format";

export const CHANNELS = ["push", "email", "sms"];

// Characters a channel shows before it gets awkward: a push is cut off on the
// lock screen, an SMS over 160 goes out as several. A hint, not a limit: the
// API takes longer text and the form has never blocked it. An SMS's real limit
// depends on its text (smsSegmentLimit); messageCharLimit picks it.
export const CHAR_LIMITS = { push: 200, sms: 160 };

// UCS-2 (UTF-16) holds 70 characters per SMS, against GSM-7's 160.
export const SMS_UCS2_LIMIT = 70;

// GSM 03.38 basic character set (without ESC), then its extension table,
// whose characters are sent through ESC and so stay GSM-7.
const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENSION = "\f^{}\\[~]|€";
const GSM_CHARS = new Set([...GSM_BASIC, ...GSM_EXTENSION]);

// Whether every character of the text can go out as GSM-7.
export const isGsm7 = (text) =>
  [...(text || "")].every((char) => GSM_CHARS.has(char));

// Characters per SMS for this text: 160 in GSM-7, 70 as soon as one character
// (ş, ł, Greek lower case, an emoji, ...) forces the whole SMS into UCS-2.
export const smsSegmentLimit = (text) =>
  isGsm7(text) ? CHAR_LIMITS.sms : SMS_UCS2_LIMIT;

// The counter's limit for a channel and text; undefined for email.
export const messageCharLimit = (channel, text) =>
  channel === "sms" ? smsSegmentLimit(text) : CHAR_LIMITS[channel];

const DAY_MS = 24 * 60 * 60 * 1000;

const toMs = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// When a message goes (or went) out: GREATEST(created_at, scheduled_at), the
// same definition as the backend's Advanced dashboard (advanced.rb). A message
// with no scheduled_at, or one scheduled in the past, was sent on creation.
export function messageSendTime(message) {
  const created = toMs(message?.created_at);
  const scheduled = toMs(message?.scheduled_at);
  if (created === null && scheduled === null) return null;
  return new Date(
    Math.max(created ?? -Infinity, scheduled ?? -Infinity),
  ).toISOString();
}

// The two summary tiles: messages sent in the last 30 days, and messages
// still waiting to go out.
export function marketingSummary(messages, now = new Date()) {
  const since = now.getTime() - 30 * DAY_MS;
  let sent = 0;
  let scheduled = 0;
  for (const message of messages || []) {
    if (message.status === "pending") {
      scheduled += 1;
    } else if (message.status === "sent") {
      const at = toMs(messageSendTime(message));
      if (at !== null && at >= since && at <= now.getTime()) sent += 1;
    }
  }
  return { sent, scheduled };
}

// HTML (an email body) -> plain text, for the list and labels.
export function stripHtml(html) {
  if (!html) return "";
  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Whether an email body has anything to send: text, or an image (an
// image-only body strips to "" but is not empty).
export function hasMessageContent(html) {
  if (!html) return false;
  return /<img\b/i.test(html) || stripHtml(html) !== "";
}

// Subject for an email, otherwise the text itself.
export function messageTitle(message) {
  if (message?.message_type === "email" && message.subject)
    return message.subject;
  return stripHtml(message?.message || "");
}

export const passName = (pass) => pass?.name || pass?.pass?.name || "";

// "All cards", "VIP card", or "At risk" / "At risk · VIP card" for a segment push.
export function audienceLabel(message, t) {
  const card = message?.pass ? passName(message.pass) : null;
  if (message?.segment) {
    const segment = t(segmentLabelKey(message.segment));
    return card ? `${segment} · ${card}` : segment;
  }
  return card || t("marketing.form.audienceAll");
}

// Which kind of audience a message has, for the pill colour and the form.
export function audienceKind(message) {
  if (message?.segment) return "segment";
  if (message?.pass || (message?.pass_id && message.pass_id !== "all"))
    return "card";
  return "all";
}

// The integrations that serve a channel: those tagged for it ("email" /
// "sms"). Push needs none, so it has none. The one rule for the provider
// select, the single-provider auto-pick and the channel lock.
export const providersFor = (channel, integrations) =>
  channel === "push"
    ? []
    : (integrations || []).filter((integration) =>
        integration.tags?.includes(channel),
      );

// Whether the composer lets the merchant pick a channel (decision M2):
// - push is always open;
// - email/SMS with a connected provider tagged for it are open on any plan
//   (a store that dropped below Advanced keeps the integration it set up);
// - without one, an Advanced store (trial included) can pick the channel and
//   is pointed at Integrations ("noProvider");
// - without one, below Advanced the card is locked and opens the plan dialog.
export function channelAccess(channel, { integrations, tier }) {
  if (channel === "push") return "open";
  if (providersFor(channel, integrations).length > 0) return "open";
  return tierAllows(tier, "advanced") ? "noProvider" : "locked";
}

// Day, month and clock for a message time in the list and the view drawer.
export const WHEN_FORMAT = {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
};

// "1,204" when the API counted it (a sent segment push); for a message still
// waiting, a note that it is counted at send time; otherwise a dash.
export function recipientsText(message, t, language) {
  if (Number.isFinite(message.recipient_count)) {
    return formatNumber(message.recipient_count, language);
  }
  if (message.status === "pending") return t("marketing.recipients.pending");
  return "—";
}

export function channelName(type, t) {
  return t(`marketing.channels.${type}.name`);
}

// The provider's display name ("Sendgrid", "Twilio"): the label the composer's
// provider select shows.
export const providerLabel = (integration) =>
  integration.integration_type
    ? integration.integration_type.charAt(0).toUpperCase() +
      integration.integration_type.slice(1)
    : String(integration.id);

export const FAILURE_REASONS = [
  "no_recipients",
  "provider_rejected",
  "delivery_exception",
];

// Why a message failed (MessageBlueprint's failure_reason): one of
// FAILURE_REASONS, or "unknown" for a failed message with no recorded reason.
// Null unless the message failed. A missing field (an API that predates it)
// reads as null; so does a reason this client doesn't know, and a push marked
// no_recipients, which the backend never writes (push consent is the wallet's).
export function failureReason(message) {
  if (message?.status !== "failed") return null;
  const reason = message.failure_reason;
  if (!FAILURE_REASONS.includes(reason)) return "unknown";
  if (reason === "no_recipients" && message.message_type === "push")
    return "unknown";
  return reason;
}

// The copy for a failed message: a one-line hint for the table (none when the
// reason is unknown, since the Failed pill already says it), the explanation
// and what to do for the view drawer. Null unless the message failed.
export function failureCopy(message, t) {
  const reason = failureReason(message);
  if (!reason) return null;
  if (reason === "no_recipients") {
    return {
      reason,
      hint: t("marketing.failure.noRecipients.hint"),
      detail: t(
        message.message_type === "sms"
          ? "marketing.failure.noRecipients.sms"
          : "marketing.failure.noRecipients.email",
      ),
      action: t("marketing.failure.noRecipients.action"),
    };
  }
  if (reason === "provider_rejected") {
    const provider = message.integration?.integration_type
      ? providerLabel(message.integration)
      : null;
    return {
      reason,
      hint: provider
        ? t("marketing.failure.providerRejected.hint", { provider })
        : t("marketing.failure.providerRejected.hintUnnamed"),
      detail: provider
        ? t("marketing.failure.providerRejected.detail", { provider })
        : t("marketing.failure.providerRejected.detailUnnamed"),
      action: t("marketing.failure.providerRejected.action"),
    };
  }
  if (reason === "delivery_exception") {
    return {
      reason,
      hint: t("marketing.failure.deliveryException.hint"),
      detail: t("marketing.failure.deliveryException.detail"),
      action: t("marketing.failure.deliveryException.action"),
    };
  }
  return {
    reason,
    hint: null,
    detail: t("marketing.failure.unknown.detail"),
    action: null,
  };
}
